// frontend/src/auth/canonicalAppLaunch.ts
//
// CRM's transport for Central's canonical same-workspace app launch
// (`POST /api/suite/launch-intent`), consumed by ui-shell 1.15.0's
// `config.requestAppLaunch`.
//
// WHAT THIS REPLACES
// ------------------
// The app switcher navigated to `app.launchUrl ?? app.webUrl`, read out of
// the boot `apps[]` snapshot. `webUrl` is the app's canonical DISPLAY url,
// not an authenticated entry point, so the fallback dropped the user on an
// app that had no idea who they were. And `apps[]` is a snapshot —
// entitlement, admission and OAuth-client registration can all have changed
// since boot, so the shell could offer a launch Central would refuse and
// leave the destination app to say no after a successful login.
//
// Central validates all of that and returns the app's own entry point, which
// since Central PR #837 is produced by the same `decideLaunch` the workspace
// switch uses.
//
// A PIPE, not a decision point. Central's answer is returned verbatim; CRM
// never repairs it, never re-resolves it, and never constructs a URL. There
// is no best-effort launch for a first-party app.

import type { CanonicalLaunchDecision } from '@levelup/ui-shell';
import { CENTRAL_URL } from '../config/centralAuth';

export async function requestCanonicalAppLaunch(
  appId: string,
): Promise<CanonicalLaunchDecision> {
  if (!appId) {
    return { launchable: false, reason: 'unknown-app' };
  }

  const base = (CENTRAL_URL || '').replace(/\/+$/, '');
  if (!base) {
    // Without Central's origin there is no authority to ask. Fail closed
    // rather than falling back to a CRM-constructed URL.
    return { launchable: false, appId, reason: 'central-url-not-configured' };
  }

  let res: Response;
  try {
    res = await fetch(`${base}/api/suite/launch-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      // NO workspaceId. Central derives it from the session, deliberately,
      // so an app cannot assert a workspace it does not hold. `surface` is
      // provenance only and never gates the launch.
      body: JSON.stringify({ appId, surface: 'crm-app-switcher' }),
    });
  } catch {
    return { launchable: false, appId, reason: 'central-unreachable' };
  }

  try {
    return (await res.json()) as CanonicalLaunchDecision;
  } catch {
    return { launchable: false, appId, reason: 'central-unreachable' };
  }
}
